<template>
  <transition name="fade-filters" appear>
    <div class="filters">
      <StickyMenu
        ref="stickyMenu"
        class="filters__inner"
        :isActive="!isMobile"
        @mounted="onStickyMenuMounted"
      >
        <TitleBox />

        <!-- Mobile Filtering and Sorting -->
        <div class="filters__bar filters__bar--mobile" v-if="isMobile">
          <div class="filters__filter-trigger" @click="showExploreFilterOverlay">
            <span class="filters__filter-icon icon-filter"></span>
            {{ $t('filter.filter_results') }}
          </div>
          <div class="filters__sort-trigger" @click="showExploreSortOverlay">
            <span class="filters__sort-icon icon-sort"></span>
            {{ $t('filter.sorting.sort_results') }}
          </div>
        </div>

        <!-- Desktop Filtering -->
        <div class="filters__primary" v-if="!isMobile">
          <PrimaryFilter
            v-for="(primary, i) in primaryFilters"
            :key="primary.id + i"
            :filter="primary"
            :isFirstFilter="i === 0"
            @height-change="onAccordionHeightChange"
            @height-change-end="onAccordionHeightChangeEnd"
          />
        </div>
        <div class="filters__secondary" v-if="!isMobile" v-show="visibleSecondaryFilters.length">
          <div class="filters__secondary-title">
            <span class="filters__filter-icon icon-filter"></span>
            {{ $t('filter.filter') }}
          </div>
          <Accordion>
            <SecondaryFilter
              v-for="(secondary, i) in visibleSecondaryFilters"
              :key="secondary.id + i"
              :filter="secondary"
              :index="i"
              @height-change="onAccordionHeightChange"
              @height-change-end="onAccordionHeightChangeEnd"
            />
          </Accordion>
        </div>
      </StickyMenu>
    </div>
  </transition>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import { EventBus, EVENTS } from '@/services/event-bus'
import { scrollSmoothlyToBottom } from '@/helpers/scroll'
import { CARDS_TRANSITION_TIME } from '@/components/_shared/Cards/Cards.vue'
import StickyMenu from '@/components/_shared/StickyMenu/StickyMenu.vue'
import Accordion from '@/components/_shared/Accordion.vue'
import TitleBox from './TitleBox/TitleBox.vue'
import PrimaryFilter from './PrimaryFilter/PrimaryFilter.vue'
import SecondaryFilter from './SecondaryFilter/SecondaryFilter.vue'

export default {
  name: 'Filters',

  components: {
    StickyMenu,
    Accordion,
    TitleBox,
    PrimaryFilter,
    SecondaryFilter,
  },

  computed: {
    ...mapState('filter', ['activeFilters']),

    ...mapGetters({
      isMobile: 'filter/isMobile',
      shuffledProductsPromos: 'filter/shuffledProductsPromos',
      visibleSecondaryFilters: 'filter/visibleSecondaryFilters',
      primaryFilters: 'filter/primaryFilters',
    }),
  },

  watch: {
    isMobile: {
      immediate: true,
      handler(isMobile) {
        if (isMobile) {
          this.preloadExploreFilterOverlay()
          this.preloadExploreSortOverlay()
        } else {
          this.hideExploreFilterOverlay()
          this.hideExploreSortOverlay()
        }
      },
    },

    shuffledProductsPromos(cards) {
      const menu = this.$refs.stickyMenu?.getInstance()

      if (!this.isMobile && menu && !cards.length) {
        menu.parentNode.style.minHeight = `${menu.parentMaxHeight}px`
        if (menu.isStickingToBottom) {
          scrollSmoothlyToBottom(menu.$el, { duration: CARDS_TRANSITION_TIME, scrollUpOnly: true })
        }
      }
    },

    activeFilters() {
      this.$refs.stickyMenu?.afterHeightChange()
    },
  },

  mounted() {
    EventBus.$on(EVENTS.filter.beforeSwitchCards, this.beforeSwitchCards)
  },

  beforeDestroy() {
    EventBus.$off(EVENTS.filter.beforeSwitchCards, this.beforeSwitchCards)
  },

  methods: {
    ...mapMutations('filter', ['setIsStickyMenuMounted', 'setStickyMenuComponent']),

    ...mapActions('overlay', [
      'preloadExploreFilterOverlay',
      'preloadExploreSortOverlay',
      'showExploreFilterOverlay',
      'showExploreSortOverlay',
      'hideExploreFilterOverlay',
      'hideExploreSortOverlay',
    ]),

    beforeSwitchCards() {
      const menu = this.$refs.stickyMenu?.getInstance()
      if (menu) {
        menu.scrollToTop()
        menu.parentNode.style.minHeight = ''
      }
    },

    onStickyMenuMounted() {
      this.setIsStickyMenuMounted(true)
      this.setStickyMenuComponent(this.$refs.stickyMenu)
    },

    onAccordionHeightChange(additionalHeight) {
      this.$refs.stickyMenu.beforeHeightChange(additionalHeight, false)
    },

    onAccordionHeightChangeEnd() {
      this.$refs.stickyMenu.afterHeightChange()
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Filters';
</style>
